import { AccessOption } from '@tock/shared/utils/offerings/experienceAccessOptions';
import { Filters as ChitFilters } from 'components/TablesReportFilter';
import { OptimizationType } from 'components/inventory-schedule/optimization/summary/optimizationSummaryFormAux';
import { PartialPostVisitFeedbackFilters } from 'components/reports/insights/PostVisitFeedbackReportFilters';
import { PartyDragSource } from 'components/service/party/PartyDrag';
import { StoreType } from '@tock/shared/utils/connect';
import { TableAssignmentPriorityLevel } from 'generated/graphql';
import { WaitlistFilters } from 'state/serviceReports';
import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import HandSellType from 'constants/HandSellType';

type UserPropertyBrowser = { property: 'Browser type'; value: string };
type UserPropertyBrowserVersion = { property: 'Browser version'; value: string };
export type UserPropertyDeviceApp = {
  property: 'Device app type';
  value: 'Web' | 'Tock-Dashboard App (iOS)';
};
type UserPropertyPatronId = { property: 'PatronId'; value: number };
type UserPropertyTockRole = { property: 'Tock role'; value: core.GlobalRoles.Role[] };
type UserPropertyBusinessType = { property: 'Business type'; value: 'Flex' | 'Slot' };
type UserPropertyViewPortWidth = { property: 'Viewport width'; value: number };
type UserPropertyViewPortHeight = { property: 'Viewport height'; value: number };
type UserPropertyMobileViewSetting = { property: 'Mobile view setting'; value: boolean };

export type UserProperty =
  | UserPropertyBrowser
  | UserPropertyBrowserVersion
  | UserPropertyDeviceApp
  | UserPropertyPatronId
  | UserPropertyTockRole
  | UserPropertyBusinessType
  | UserPropertyViewPortWidth
  | UserPropertyViewPortHeight
  | UserPropertyMobileViewSetting;

export type EventTimedAction = ReservationModalType | ServerPanelType | HelpMenuType;

// A NOTE on naming conventions:
// We prefer to capitalize the first word in the event name which gets
// sent to Amplitude. (ie 'Click add brand' > 'click add brand')
// However, there are some legacy events below which no not follow
// this convention. They have been left as is for consistency
// in Amplitude.
// actions

export enum EventAction {
  ACTION_VIEW_PAGE = 'View page',

  HELP_WHATS_NEW = "View what's new",
  HELP_COOKIE_PREF = 'Click cookie preferences',
  HELP_OPEN_DRAWER = 'Open help drawer',
  HELP_ZENDESK_LINK = 'View Zendesk article',
  HELP_SEARCH_ZENDESK = 'Search Zendesk',
  HELP_OPEN_CHAT = 'Open chat widget',
  HELP_SUPPORT_OPTION_TAB = 'View support option tab',
  HELP_NEED_TRAINING = 'Click need more training',
  NAVIGATE_HELP_MENU_TAB = 'Switch tabs in help menu drawer',

  SETTINGS_TOGGLE_MOBILE_VIEW = 'Toggle mobile view setting',

  AVAILABILITY_OPEN_DRAWER = 'Open availability drawer',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  BOOKING_TRANSFER = 'Click transfer',
  BOOKING_COMP = 'Click comp booking',
  BOOKING_MARK_NO_SHOW = 'Click no show',
  BOOKING_EDIT_WALK_IN = 'Click edit walk-in ',
  BOOKING_CANCEL_WALK_IN = 'Cancel walk-in',
  BOOKING_CANCEL_PURCHASE = 'Click cancel purchase',
  BOOKING_SMS = 'Click SMS',
  BOOKING_PRINT_CHIT = 'Click print chit',
  BOOKING_OVERBOOKING_TOGGLE_ON = 'Enable overbooking toggle',
  BOOKING_CONFIRM_OVERBOOKING = 'Click confirm overbooking',
  BOOKING_CONFIRM_OVERBOOKING_TIME_SLOT = 'Click confirm overbooking on time slot',
  BOOKING_SEARCH_EDIT_PARTY_SIZE = 'Reservation search edit party size',
  BOOKING_EDIT_EXPERIENCE = 'Change reservation experience',
  BOOKING_EDIT_PARTY_SIZE = 'Change reservation party size',
  BOOKING_EDIT_TURN_TIME = 'Change reservation turn time',
  BOOKING_EDIT_RESERVATION_TIME = 'Change reservation start time',
  BOOKING_EDIT_RESERVATION_DATE = 'Change reservation date',
  BOOKING_EDIT_SEATING_AREA = 'Change seating area',
  BOOKING_EDIT_SEATING_AREA_FILTER_CHANGE = 'View seating area availability',
  BOOKING_CANCEL = 'Cancel booking',
  BOOKING_OVERBOOKING_CREATED = 'Create overbooking',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  BOOKING_ASSIGN_TABLE = 'Booking added to additional table',
  BOOKING_REMOVE_TABLE = 'Booking removed from additional table',
  BOOKING_REPLACE_TABLE = 'Booking table replaced with new table',
  MODIFY_IN_FLIGHT_RESERVATION = 'Quick modify reservation details',

  CUISINE_TAGS_EDIT = 'Edit cuisine tags',

  CC_HOLD_CHARGE = 'Charge credit card hold',
  CC_HOLD_REFUND = 'Refund credit card hold',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  CALENDAR_ITEM_QUICK_ACTION = 'Inventory quick action clicked',

  DAILY_CALENDAR_MODIFY_ACTIONS_MODAL_TRIGGERED = 'Modify actions modal triggered in daily calendar view',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  DATA_GRID_COLUMN_RESIZED = 'Report data grid column resized',

  EDIT_IN_SERVICE = 'Edit in-service party details',
  EDIT_GUEST_NOTE = 'Edit guest profile note',
  EDIT_GUEST_DIETARY_NOTE = 'Edit guest profile dietary note',
  EDIT_GUEST_TAGS = 'Edit guest profile tags',
  EDIT_VISIT_TAGS = 'Edit visit tags',
  OPEN_TAG_DIALOG = 'Open tag dialog',

  EVENTS_CREATE_EVENT = 'Create event',
  EVENTS_CHANGE_ACCESS = 'Change event access',
  EVENTS_CHANGE_DETAILS = 'Change event details',

  ACCOUNT_REQUEST_UPGRADE = 'Request account upgrade',

  ADMIN_ADD_EXPERIENCE = 'Start experience creation',
  ADMIN_CREATE_EXPERIENCE = 'Submit new experience',
  ADMIN_SELECT_FLAT_PRICING = 'Select flat pricing for experience',
  ADMIN_SELECT_RANGED_PRICING = 'Select ranged pricing for experience',
  ADMIN_ADD_SINGLE_RANGED_PRICE = 'Add a ranged price for experience',
  ADMIN_DELETE_SINGLE_RANGED_PRICE = 'Delete a ranged price for experience',
  ADMIN_SAVE_EXPERIENCE_CHANGES = 'Save changes to experience',
  ADMIN_HANDSELL_VIEW_CC_FIELDS = 'Render Stripe payment element during handsell',

  // TACO
  CREATE_SCHEDULE = 'Start schedule creation',
  EDIT_SCHEDULE = 'Start schedule edit',
  PUBLISH_SCHEDULE = 'Publish schedule',
  PUBLISH_SCHEDULE_MANAGE_TIMELINE_BLOCKS = 'Manage timeline blocks on schedule publish',
  UNPUBLISH_SCHEDULE = 'Unpublish schedule',
  SAVE_SCHEDULE_AS_DRAFT = 'Save schedule as draft',
  SCHEDULE_DETAILS = 'Go to Schedule Flow Details',
  SCHEDULE_FLOORPLAN = 'Go to Schedule Flow Floorplan',
  SCHEDULE_SERVICES = 'Go to Schedule Flow Services',
  SCHEDULE_REVIEW = 'Go to Schedule Flow Review',
  SCHEDULE_DOWNLOAD_UNASSIGNED = 'Download unassigned tables',
  SCHEDULE_ADD_EXPERIENCE_TO_TABLE = 'Add experience to schedule table',
  SCHEDULE_BULK_EDIT_TABLE_EXPERIENCES = 'Bulk edit experiences on schedule tables',
  SCHEDULE_ADD_EXPERIENCE_TO_NEW_TABLE = 'Add experience to newly added schedule tables',
  START_CREATE_SERVICE = 'Start create service',
  SCHEDULE_CREATE_SERVICE = 'Create new service',
  SCHEDULE_COPY_SERVICE = 'Duplicate existing service in a schedule',
  CHANGE_SCHEDULE_START_DATE = 'Change schedule start date',
  CHANGE_SCHEDULE_END_DATE = 'Change schedule end date',
  CHANGE_EXPERIENCE_TABLE_TIMES = 'Change experience table times',
  CREATE_FLOORPLAN_TABLES = 'Create floorplan tables',
  START_CREATE_SINGLE_FLOORPLAN_TABLE = 'Start create single floorplan table',
  START_CREATE_MULTIPLE_FLOORPLAN_TABLE = 'Start create multiple floorplan tables',
  START_EDIT_MULTIPLE_FLOORPLAN_TABLES = 'Start bulk edit floorplan tables',
  START_EDIT_MULTIPLE_SERVICE_TABLES = 'Start bulk edit service tables',
  VIEW_FLOORPLAN_TABLE_COMBINATIONS = 'View floorplan table combinations',
  VIEW_SERVICE_TABLE_COMBINATIONS = 'View service table combinations',
  IMPORT_SERVICE = 'Import service',
  VIEW_ADVANCED_GRID = 'View availability in advanced grid view',
  VIEW_TABLE_COMBINATIONS = 'View table combinations',
  RESET_TABLE_COMBINATION_EXPERIENCES = 'Reset table combination experiences',
  OPEN_OPTIMIZATIONS_DRAWER = 'Open optimizations drawer',
  CREATE_OPTIMIZATION = 'Create optimization',
  EDIT_OPTIMIZATION = 'Edit optimization',
  DELETE_OPTIMIZATION = 'Delete optimization',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  INTERNAL_MENU_DELETE = 'Internal menu delete',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  MENU_ADD_ITEMS = 'Adds a number of existing menu items to menu',
  MENU_CREATE_ITEM = 'Adds a new menu item to menu',
  MENU_PREVIEW_EXPERIENCE = 'Menu preview experience',
  MENU_CREATE_MODIFIER_GROUP = 'Menu create modifier group',
  MENU_UPDATE_LINKAGE = 'Menu update linked experiences',
  MENU_VIEW_HOW_TO_CONTENT = 'Menu view how-to content',

  OUTDOOR_TAG_EXPERIENCE_CHECKED = 'Outdoor tag experience checked',
  OUTDOOR_TAG_EXPERIENCE_UNCHECKED = 'Outdoor tag experience unchecked',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  PARTY_ARROW_KEY_SELECTION = 'Party selected via arrow key',
  PARTY_MOVE_STARTED = 'Party move started',
  PARTY_TABLE_ASSIGNMENT_STARTED = 'Party table assignment started',
  PARTY_TABLE_ASSIGNMENT_DISMISSED = 'Party table assignment dismissed',
  PARTY_SEATED = 'Party seated',
  PARTY_FINISHED = 'Party finished',
  PARTY_SECONDARY_STATE_CHANGED = 'Party secondary state changed',
  PARTY_TABLE_PREASSIGNED = 'Party table preassigned',
  PARTY_CHANGE_PREASSIGNMENT_STARTED = 'Party change preassignment started',
  PARTY_QUICK_NOTE_EDITED = 'Party quick note edited',
  PARTY_GUEST_PROFILE_TAGS_EDITED = 'Party guest profile tags edited',
  PARTY_UNASSIGNED_SELECTED_POST_ACTION = 'Unassigned party selected post action',
  PARTY_STATUS_QUICK_ACTION_OPENED = 'Party status quick action opened',

  PARTY_CARD_VIEW_GUEST = 'Party card guest viewed',
  PARTY_CARD_VIEW_GUEST_FOR_PREASSIGN = 'Party card guest viewed for subsequent preassigning',

  // Visit details, UI that is triggered from the party panel sidepanel
  VIEW_FULL_PARTY_PANEL = 'View full party panel',
  CHANGE_FULL_PARTY_PANEL_TAB = 'Change full party panel tab',

  // Visit details, UI that is triggered from the guests search UI
  VIEW_PURCHASE_DETAILS_MODAL = 'View purchase details modal',
  CHANGE_PURCHASE_DETAILS_MODAL_TAB = 'Change purchase details modal tab',

  // Resending confirmation email
  INITIATE_RESENDING_CONFIRMATION_EMAIL = 'Initiate resending confirmation email',
  COMPLETE_RESENDING_CONFIRMATION_EMAIL = 'Complete resending confirmation email',
  CANCEL_RESENDING_CONFIRMATION_EMAIL = 'Cancel resending confirmation email',

  PRICE_RANGE_UPDATE = 'Price range update',

  RESYNC = 'Dashboard full resync requested',

  SIDE_PANEL_TOGGLE_OPEN = 'Side panel toggle to open',

  TAG_DELETE = 'Delete a visit/guest tag',
  CANCEL_TAG_DELETE = 'Cancel a visit/guest tag delete',

  TIMELINE_DAY_VIEW_SWITCH = 'Timeline switched to day view',
  TIMELINE_MONTH_VIEW_SWITCH = 'Timeline switched to month view',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  TIMELINE_ALL_SECTIONS_TOGGLED = 'Timeline all sections toggled',
  TIMELINE_SECTION_TOGGLED = 'Timeline section visibility toggled',
  TIMELINE_SHOW_PRIORITY = 'Timeline show priority',
  TIMELINE_SHOW_GUEST_LIST_TIME_SLOT_HEADERS_PREFERENCE_CHANGED = 'Timeline guest list time slot headers preference changed',
  TIMELINE_TABLE_SORT = 'Timeline table sort',
  TIMELINE_VIEW_CHANGED = 'Timeline selected inventory view changed',
  TIMELINE_UNASSIGNED_SECTION_TOGGLED = 'Timeline unassigned section visibility toggled',
  TIMELINE_HOURLY_VIEW_PREFERENCE_CHANGED = 'Timeline hourly view preference changed',

  SERVER_SEARCH = 'Search for a server',

  WALK_IN_SEATING_STARTED = 'Walk-in seating started',
  WALK_IN_PROFILE_VISITED = 'Walk-in profile visited',
  WALK_IN_PANEL_NEW = 'Open new walk-in panel',
  WALK_IN_PANEL_EDIT = 'Edit walk-in',
  WALK_IN_CLOSE = 'Close walk-in',
  WALK_IN_ADDED = 'Created new walk-in',
  WALK_IN_UPDATED = 'Updated Walk-in',

  WAITLIST_START_ADD_FLOW = 'Start add guest to waitlist flow',
  WAITLIST_ADD = 'Add guest to waitlist',
  WAITLIST_EDIT = 'Edit waitlist entry',
  WAITLIST_EMAIL_SEND = 'Email reservation to waitlist guests',
  WAITLIST_BOOK_VIA_EMAIL = 'Book waitlist reservation via email',
  WAITLIST_BOOK_RESERVATION = 'Book waitlist reservation',
  WAITLIST_SELECT_EXACT_MATCHES = 'Select exact waitlist matches',
  WAITLIST_SELECT_UNNOTIFIED_MATCHES = 'Select unnotified waitlist matches',
  WAITLIST_SELECT_ALL_MATCHES = 'Select all waitlist matches',
  WAITLIST_SELECT_ALL_GDA_MATCHES = 'Select all Global Dining Access members',
  WAITLIST_DESELECT_ALL_MATCHES = 'Deselect all waitlist matches',
  WAITLIST_ESTIMATED_WAIT_TIME_VALUE_CHANGED = 'Selected a different quoted time from the model predicted',

  SELECT_TABLE_PRIORITY = 'Select table priority',

  ASSIGN_PARTY_TO_TABLE = 'Assign party to table',
  CANCEL_TABLE_ASSIGNMENT = 'Cancel table assignment',
  CLOSE_ONLINE_BOOKINGS = 'Close online bookings',

  TIMING_SERVICE_VIEW_SELECTED = 'Timing - service view selected',
  TIMING_SERVICE_AERIAL_OVERLAY_ON = 'Timing - service aerial overlay on',
  TIMELINE_GUEST_NEEDS = 'Select guest needs',

  // Don't pattern match on these event naming patterns
  // events should follow the format `[verb] [action]`
  // no passive voice! And avoid UI specific verbs like "click"
  SEATING_AREAS_LEARN_MORE = 'Seating areas learn more clicked',
  SELECT_SEATING_AREA = 'Select seating area',
  APPLY_SEATING_AREAS = 'Apply seating areas',
  SCHEDULE_SEATING_AREAS_ENABLED = 'Seating areas enabled for a schedule',
  SCHEDULE_SEATING_AREAS_DISABLED = 'Seating areas disabled for a schedule',
  SEATING_AREAS_CANCEL_ASSIGN = 'Seating area assignment cancelled',
  TIMELINE_DRAG_SEATING_AREA_MISMATCH = 'Timeline drag seating area mismatch',
  TIMELINE_DRAG_SEATING_AREA_MISMATCH_CANCEL = 'Timeline drag seating area mismatch cancel',

  // Operations
  OPERATIONS_DOWNLOAD_REPORT = 'Download operations report',
  OPERATIONS_PRINT_REPORT = 'Print operations report',

  OPERATIONS_REVIEWS_VIEW_REPORT = 'View reviews report',
  OPERATIONS_PICKUP_VIEW_REPORT = 'View pickup report',
  OPERATIONS_DELIVERY_VIEW_REPORT = 'View delivery report',
  OPERATIONS_RESERVATIONS_VIEW_REPORT = 'View reservations report',
  OPERATIONS_PRE_SERVICE_VIEW_REPORT = 'View pre-service report',
  OPERATIONS_WAITLIST_VIEW_REPORT = 'View waitlist report',

  TABLES_REPORT_US_STATES_UPDATED = 'US states in purchase report updated',

  USER_ACTIVITY_TRACKING = 'User interaction',

  CLICK_VIEW_CHITS = 'View chits',

  // Control Panel > Experiences > Communication
  COMS_POST_VISIT_SETTING_TOGGLE = 'Toggle post-visit communications',
  // Control Panel > Experiences > Policies
  UPDATE_GUEST_MODIFICATION_POLICY = 'Update guest modification policy',
  // Control Panel > Experiences > Pre-visit questions
  PRE_VISIT_QUESTIONS_PREVIEW = 'Preview pre-visit questions',
  PRE_VISIT_QUESTIONS_ADD_QUESTION = 'Add pre-visit question',
  PRE_VISIT_QUESTIONS_EDIT_QUESTION = 'Edit pre-visit question',

  // Guest Search
  GUEST_SEARCH_SELECT = 'Select guest search result',
  GUEST_SEARCH_SUBMIT = 'Submit guest search',

  // Guest directory
  GUEST_DIRECTORY_VIEW = 'View guest directory',
  GUEST_DIRECTORY_DOWNLOAD = 'Download guest directory',
  GUEST_DIRECTORY_SELECT_GUEST = 'Select guest(s)',
  GUEST_DIRECTORY_DESELECT_GUEST = 'Deselect guest(s)',
  GUEST_DIRECTORY_MERGE_GUEST = 'Merge guests',
  GUEST_DIRECTORY_DELETE_GUEST = 'Delete guest(s)',
  GUEST_DIRECTORY_EDIT_GUEST = 'Edit guest',

  // Marketing
  MARKETING_OPT_IN_DOWNLOAD = 'Download marketing opt-in guests',

  // Integrations
  CONNECT_INTEGRATION = 'Connect integration',
  DISCONNECT_INTEGRATION = 'Disconnect integration',
  CHANGE_RWG_OPTION = 'Change Reserve with Google options',

  // Actions that can occur in multiple locations
  VIEW_GUEST_PROFILE = 'View guest profile details',

  //Specific assign server EventAction. This is to help distinguish assigning a server form the serverPanel vs the tablePanel.
  ASSIGN_SERVER_TO_TABLE = 'Assign sever to table',

  // Account Page
  SAVE_ACCOUNT_PERSONAL_DETAILS = 'Save account personal details',
  BEGIN_MFA_CHANGE = 'Begin MFA change',
  RESEND_MFA_CODE = 'Resent MFA code',
  COMPLETE_MFA_CHANGE_SUCCESS = 'Complete MFA change successfully',
  FAIL_MFA_CHANGE = 'Fail to complete MFA change',
  ABANDON_MFA_CHANGE = 'Abandon MFA change',
  RESET_PASSWORD = 'Reset password',
}

export enum EventAttributesCategory {
  BOOKING = 'Booking',
  INVENTORY = 'Inventory',
  OPERATIONS = 'Operations',
}

export enum EventAttributesActionLocation {
  BODY = 'Body',
  HEADER = 'Header',
  IMAGE = 'Image',
  RESERVATIONS_HEADER = 'ReservationsHeader',

  // Timeline & Service
  GUEST_PANEL = 'Guest panel',
  ADMIN_CONTROL_PANEL = 'Admin control panel',
  RESERVATION_DETAIL_TAB = 'Reservation detail tab',
  RECEIPT_TAB = 'Receipt tab',
  PURCHASE_DETAILS_MODAL = 'Purchase details modal (accessed from guest search)',
  BOOKING_CANCEL_MODAL = 'Cancel reservation modal',
  BOOKING_EDIT_MODAL = 'Edit reservation modal',
  BOOKING_NEW_MODAL = 'New reservation modal',
  BOOKING_RESCHEDULE_MODAL = 'Reschedule reservation modal',
  BOOKING_SEARCH_EXPERIENCE_TABLE = 'Reservation search on table as experiences',
  BOOKING_DETAILS_MODAL = 'Booking details modal',
  BULK_EDIT_DIALOG = 'Bulk edit dialog',
  CRM_SIDE_PANEL = 'CRM side panel',
  ADJUST_PACING_PANEL = 'Adjust pacing panel',
  BLOCK_ONLINE_AVAILABILITY_PANEL = 'Block online availability panel',
  EXPERIENCE_NEW_MODAL = 'New experience modal',
  INVENTORY_MODAL = 'Inventory modal',
  MENUS_PAGE = 'Menus page',
  MENU_ITEMS_PAGE = 'Menu items page',
  MENU_ITEM_MODIFIERS_PAGE = 'Menu item modifiers page',
  PARTY_PANEL = 'Party panel',
  PARTY_POPOVER = 'Party Popover',
  TABLE_PANEL = 'Table panel',
  TABLE_POPOVER = 'Table Popover',
  WALK_IN_PANEL = 'WalkIn panel',
  WAITLIST_PANEL = 'Waitlist panel',
  INTERNAL_MENU_DELETE_MODAL = 'Internal menu delete modal',
  TIMELINE_BODY = 'Timeline body',
  SERVICE_BODY = 'Service body',
  TABLE_CONTEXT_MENU = 'Table context menu',
  SERVER_PANEL = 'Server panel',

  // TACO
  SEATING_TEMPLATE_TABLE_POPOVER = 'Seating template table popover',
  SEATING_TEMPLATE_TABLE_BULK_POPOVER = 'Seating template table bulk popover',
  ADD_TABLE_DRAWER = 'Add table drawer',
  EDIT_TABLE_DRAWER = 'Edit table drawer',
  ADD_TABLES_DRAWER = 'Add tables drawer',
  ADD_TABLE_COMBINATION_DRAWER = 'Add table combination drawer',
  EDIT_TABLE_COMBINATION_DRAWER = 'Edit table combination drawer',
  EDIT_SEATING_TEMPLATE_TABLE_COMBINATION_DRAWER = 'Edit seating template table combination drawer',
  BULK_EDIT_TABLE_DRAWER = 'Bulk edit table drawer',
  ASSIGN_SEATING_AREAS_DIALOG = 'Assign seating areas dialog',
  COPY_SERVICE_DRAWER = 'Copy service drawer',
  ADD_SERVICE_POPOVER = 'Add service popover',
  ADD_SERVICE_DRAWER = 'Add service drawer',
  ADVANCED_GRID = 'Advanced grid',
  ADVANCED_GRID_DRAG = 'Advanced grid drag',
  EDIT_AVAILABILITY_PANEL = 'Edit availability panel',
  EMPTY_ROOM_MENU = 'Empty room menu',
  TABLE_ACTIONS_MENU = 'Table actions menu',
  SEATING_TEMPLATE_FLOORPLAN = 'Seating template floorplan',
  OPTIMIZATIONS_DRAWER = 'Optimizations drawer',
  SEATING_TEMPLATE_TABLE_DRAWER = 'Seating template table drawer',

  // Slot Floorplans
  TABLES_AND_ROOMS_HEADER = 'Tables and rooms header',

  // General Dashboard
  USER_NOTIFICATION = 'Direct user notification',
  GLOBAL_HEADER = 'Global header',

  // Schedules
  SCHEDULES_CONFIG = 'Schedule configuration page',
  SCHEDULES_PUBLISH_SUCCESS_MODAL = 'Schedule publish success modal',

  // Operations
  OPERATIONS_REVIEWS_REPORT = 'Operations reviews report',
  OPERATIONS_DELIVERY_REPORT = 'Operations delivery report',
  OPERATIONS_PICKUP_REPORT = 'Operations pickup report',
  OPERATIONS_PRE_SERVICE_REPORT = 'Operations pre-service report',
  OPERATIONS_WAITLIST_REPORT = 'Operations waitlist report',
  OPERATIONS_RESERVATIONS_REPORT = 'Operations reservations report',

  // Guests
  GUEST_PROFILE = 'Guest profile',
  GUEST_DIRECTORY_HEADER = 'Guest directory header',
  GUEST_DIRECTORY_SEARCH = 'Guest directory search box',
  GUEST_DIRECTORY_TABLE = 'Guest directory table',
  // Integrations
  INTEGRATIONS_PAGE = 'Integrations page',

  HELP_DRAWER_WHATS_NEW_TAB = "Help menu what's new tab",
}

// When a new reservation is made parameters are provided on availability search.
// Starting a reservation on a time slot provides table, date, and time versus when
// booking form blue book requires the user to search availability
export enum NewReservationType {
  DATE_TIME = 'New booking from an unassigned / held inventory',
  DATE_TIME_TABLE = 'New booking from a time slot',
  SEARCH_TABLE = 'Search availability for a specific table',
  SEARCH_AVAILABILITY = 'Search all availability',
  SEARCH_EXPERIENCE_TABLE = 'Reservation search on table as experiences', //Multiple Experience Per Table
}

// Reservation Modal is a modal that is opened when any modifications or new reservation are made.
// These types track for what purpose the modal was opened for.
export enum ReservationModalType {
  BOOKING_CREATE = 'Create reservation',
  BOOKING_CANCEL = 'Cancel reservation',
  BOOKING_EDIT = 'Edit reservation',
  BOOKING_RESCHEDULE = 'Reschedule reservation',
}

// Server Panel is the side panel that is used in service to manage servers and server table assignment
// These types help track how long each action take.
export enum ServerPanelType {
  SERVER_PANEL_MANAGE = 'Manage server panel', //Tracks how long server panel is open for
  SERVER_CREATE = 'Create server',
  SERVER_EDIT = 'Edit Server',
  SERVER_ASSIGN = 'Assign Server',
}

// Help Menu is the side drawer that is opened when clicking on the Help button
export enum HelpMenuType {
  HELP_DRAWER_USE = 'Use help menu', // Tracks how long help menu drawer is open for
}

export enum HelpMenuTab {
  CONTACT_US = 'contact-us',
  HELP_CENTER = 'help-center',
  WHATS_NEW = 'whats-new',
}

export type ReservationChangedDetailType =
  'time' | 'date' | 'table' | 'experience' | 'party size' | 'turn time';
export type UserActionTrackingActivity = 'timeline' | 'service';
export interface ChitInformations {
  printEmailAddresses?: boolean;
  printGuestNotes?: boolean;
  printGuestPhone?: boolean;
  printGuestTags?: boolean;
  printGuestVisitHistory?: boolean;
  printItemPrices?: boolean;
  printVisitNotes?: boolean;
  printVisitQuestions?: boolean;
  printVisitTags?: boolean;
}

// data object
export interface EventCustomAttributes {
  // global event attributes application almost all events
  category?: `${EventAttributesCategory}`;
  actionLocation?: `${EventAttributesActionLocation}`;

  // generic attributes
  toggleState?: 'opened' | 'closed';
  title?: string;
  startDate?: string;
  endDate?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  enabled?: boolean;
  mode?: string | number | Object;
  quantity?: number;
  minPartySize?: number;
  maxPartySize?: number;

  // Timing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  timingVar?: any;
  timingValue?: number;

  // User action tracking
  activity?: UserActionTrackingActivity;
  clicks?: number;
  clicksPerParty?: number;
  clicksPerSeat?: number;
  keystrokes?: number;
  keystrokesPerParty?: number;
  keystrokesPerSeat?: number;
  scrolls?: number;
  scrollsPerParty?: number;
  scrollsPerSeat?: number;
  timeActiveSec?: number;

  // search interactions, autocomplete etc
  searchTerm?: string;

  // ticket types & experiences
  ticketTypeVariety?: core.TicketType.Variety;
  accessOption?: `${AccessOption}`;
  ticketTypeId?: number;
  ticketTypeName?: string;

  // walk-in / reservation details
  hasTable?: boolean;

  // waitlist
  numParties?: number;
  waitlistId?: number;
  matchCount?: number;
  exactMatchCount?: number;
  selectedExactMatchCount?: number;
  partialMatchCount?: number;
  selectedPartialMatchCount?: number;
  unnotifiedMatchCount?: number;

  // reservation
  hasPaymentStep?: boolean;
  purchaseId?: number;
  originalPurchaseId?: number;
  overbookingType?: dashboard.OverbookingType;
  detailsChanged?: ReservationChangedDetailType[];
  experienceType?: string;
  serviceType?: core.Service.Type;
  seatingAreaName?: string;
  partySize?: number;
  reservationDateTime?: string;

  // side panel context
  currentSidePanel?: string;

  // availability and schedule
  availabilityDrawerTrigger?: 'callout' | 'icon';
  manageBlocksAction?: 'keep' | 'remove';

  // drag actions
  dragSource?: PartyDragSource;
  dragLocation?: 'timeline' | 'service';

  // events
  eventCreationMethod?: 'add event' | 'duplicate event';
  eventDetailsChanged?: (
    'name' | 'description' | 'experience tag' | 'campaign tag' | 'experience color' | 'seating area'
  )[];
  newOrExistingBusiness?: 'new business' | 'existing business';

  // menus
  menuModificationCount?: number;
  menuModifierCreationSource?: 'via menu item' | 'via modifier library';
  menuLinkageUpdateSource?: 'via settings' | 'via experience';
  menuHowToPathname?: string;

  tableViewMode?: 'calendar' | 'service' | 'snapshot' | 'guest-list';

  partyType?: 'booking' | 'walkin' | 'waitlist-request';

  tablesReportUSStates?: string;

  // Table prioritization
  selectedTablePriority?: TableAssignmentPriorityLevel;
  timelineTableSortState?: 'name' | 'priority';
  timelineShowPriorityState?: boolean;

  // FOH actions (i.e., within Timeline / Service)
  tableAssignmentLabel?: string;
  tableAssignmentStartedDetails?: {
    isAssigningTables?: boolean;
    newAssigningSession: boolean;
  };
  walkInType?: 'Walk-in' | 'Expected walk-in';
  valuesPresent?: string[];
  predictedValue?: string | number;
  quotedWaitTime?: string | number;
  actualWaitTime?: string;
  partyState?: string;
  partySecondaryState?: string;
  seatedTimeOffset?: number;
  expectedTurnTime?: number;
  actualTurnTime?: number;
  turnTimeDiffersFromDefault?: boolean;
  quotedWaitTimeDiffersFromDefault?: boolean;
  flowExecutionId?: string;
  endReason?: 'complete' | 'abort';

  eventSelfOnBoardingLabel?: string;
  calendarItemQuickAction?: string;

  // Operations
  appliedFilters?: string[];
  isInitialDateRange?: boolean;
  dateRangeSize?: number;

  // Operations - Chits

  chitInformations?: ChitInformations;
  filters?: ChitFilters;
  page?: number;

  // Operations > review report
  reviewCount?: number;
  reviewScale?: 'THREE_POINT_EMOTICON' | 'FIVE_POINT_STAR';
  reviewAverageRating?: number;
  reviewFilters?: PartialPostVisitFeedbackFilters;

  // Operations > pickup, delivery, reservations, pre-service report
  rowCount?: number;
  reportFilters?: Partial<StoreType['serviceReports']['bookingsReportFilters']>;

  // Operations > waitlist report
  waitlistFilters?: Partial<WaitlistFilters>;

  // Control Panel > Experiences > Communications
  useDefaultMessage?: boolean;

  // Downloads
  numRows?: number;
  exceedsMaxDownloadSize?: boolean;

  // Integrations
  integrationType?: string;
  rwgOption?: string;
  rwgValue?: boolean;

  // booking policies
  guestModificationsUseBusinessDefault?: boolean;
  guestModificationsUseDefaultText?: boolean;
  allowedGuestModifications?: ('cancel' | 'reschedule' | 'transfer')[];
  guestModificationWindowHours?: number;
  guestModificationPolicyFormType?: string;

  // TACO / Availability Planning
  serviceView?: 'table availability' | 'advanced grid';
  createServiceLocation?: 'drawer' | 'popover';
  bulkEditTableQuantity?: number;
  scheduleExperienceQuantity?: number;
  seatingTemplateDaysOfWeek?: string[];
  seatingTemplateId?: number;
  seatingTemplateServiceType?: string;
  scheduleState?: dashboard.BusinessInventorySchedule.State;
  isSpecialDay?: boolean;
  scheduleId?: string;
  section?: string;
  room?: string;
  optimizationType?: OptimizationType;

  // ReservationModal - Anything doing with new/editing reservations
  newReservationType?: NewReservationType;

  // Tags
  tagType?: string;
  tagInUse?: boolean;
  tagsToAdd?: string[];
  tagsToRemove?: string[];

  // Control Panel > Experiences > Pre-visit questions
  questionType?: string;
  isLibraryQuestion?: boolean;

  // Form
  closeReason?: 'explicit close' | 'submit' | string;

  // Payments
  handsellType?: HandSellType;
  guestHasBeenCharged?: boolean;
  bookingHistoryWasReviewed?: boolean;
  billingAddressFields?: boolean;

  // Help-menu notify item
  notifyItemLink?: string;
  notifyItemHasImg?: boolean;
  notifyItemHasNewChip?: boolean;
  notifyItemHeadline?: string;
  helpMenuTab?: HelpMenuTab;
}
